import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useAppFilters } from '@/hooks/shared';
import { Asset } from '@/types/models/assets.types';
import {
  AssetActionsBar,
  DataNotReceivedBadge,
  HealthStatus,
  LAST_VERIFIED_TEST_ID,
  SensorsNewIndicator,
} from '@/components/pages/AssetsPreviewPage';
import { format } from 'date-fns';
import { HEALTH_STATUS, REGULAR_DATE_FORMAT } from '@/const';
import { healthStatusFlagsToString } from '@/utils/shared';
import { FaultReasons } from '@/modules';
import { FooterSection } from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetsPreviewItems/AssetPreviewCardItem/CardSections/FooterSection';
import { Highlight } from '@/components/uiKit/Highlight';

import { HEALTH_STATUS_SCORE } from '@/constants/shared/healthStatusScore';

const getHealthStatusSortKey = (healthStatus: string, notMonitored: boolean): string => {
  const key = notMonitored ? `${healthStatus}_na` : healthStatus;
  return key.toLowerCase();
};

export const useAssetsPreviewTable = () => {
  const { assetsType } = useAppFilters();
  const columns: ColumnDef<Asset>[] = useMemo(() => {
    return [
      {
        id: 'asset_ui_name',
        header: () => <div className="whitespace-nowrap">Asset Name</div>,
        accessorKey: 'asset_ui_name',
        accessorFn: (row) => `${row.asset_ui_name}`, // Combine both for search
        size: 30,
        enableSorting: true,
        enableSearch: true,
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true; // no search term, show all
          const searchValue = (filterValue as string).toLowerCase();
          const assetName = row.original.asset_ui_name?.toLowerCase() || '';
          return assetName.includes(searchValue);
        },
        cell: ({ row, table }) => (
          <>
            {(() => {
              const searchValue = (table.options.meta as any)?.searchValue || '';
              return (
                <div className="text-sm font-semibold">
                  <Highlight text={row.original.asset_ui_name} searchTerm={searchValue} />
                </div>
              );
            })()}
          </>
        ),
      },
      {
        id: 'asset_type',
        header: () => <div className="whitespace-nowrap">Type</div>,
        accessorKey: 'asset_type',
        size: 30,
        enableSorting: true,
        cell: ({ row }) => (
          <>
            {(() => {
              return row.original.asset_type;
            })()}
          </>
        ),
      },
      {
        id: 'asset_area',
        header: () => <div className="whitespace-nowrap">Area</div>,
        accessorKey: 'asset_area',
        size: 30,
        enableSorting: true,
        cell: ({ row }) => (
          <>
            {(() => {
              return row.original.asset_area ?? 'Unknown';
            })()}
          </>
        ),
      },
      {
        id: 'health_status',
        header: () => <div className="whitespace-nowrap">Health Status</div>,
        accessorKey: 'health_status',
        size: 30,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const keyA = getHealthStatusSortKey(rowA.original.health_status, rowA.original.not_monitored ?? false);
          const keyB = getHealthStatusSortKey(rowB.original.health_status, rowB.original.not_monitored ?? false);
          const scoreA = HEALTH_STATUS_SCORE[keyA as keyof typeof HEALTH_STATUS_SCORE] ?? 0;
          const scoreB = HEALTH_STATUS_SCORE[keyB as keyof typeof HEALTH_STATUS_SCORE] ?? 0;
          // Higher score comes first (critical=9, critical_na=8, alarm=7, etc.)
          return scoreB - scoreA;
        },
        cell: ({ row }) => (
          <>
            {(() => {
              return <HealthStatus healthStatus={row.original.health_status} className="font-medium text-sm" />;
            })()}
          </>
        ),
      },
      {
        id: 'latest_diagnostic_created_at',
        header: () => <div className="whitespace-nowrap">Validation Date</div>,
        accessorKey: 'latest_diagnostic_created_at',
        size: 30,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="font-medium text-sm text-gray-900" data-testid={LAST_VERIFIED_TEST_ID}>
            {(() => {
              const lastVerified = row.original.latest_diagnostic_created_at;
              return lastVerified ? format(new Date(lastVerified), REGULAR_DATE_FORMAT) : 'N/A';
            })()}
          </div>
        ),
      },
      {
        id: 'diagnosis_remarks',
        header: () => <div className="whitespace-nowrap">Diagnosis Remarks</div>,
        accessorKey: 'diagnosis_remarks',
        size: 30,
        enableSorting: false,
        cell: ({ row }) => (
          <>
            {(() => {
              const reasons = row.original.health_status_comments || healthStatusFlagsToString(row);
              const badge = row.original.not_monitored ? (
                <DataNotReceivedBadge
                  reasonForNa={row.original?.reasons_for_na}
                  showReasonForNa={row.original?.reasons_for_na && row.original?.reasons_for_na?.length > 0}
                  fontSize="sm"
                  fontWeight="medium"
                />
              ) : (
                <FaultReasons reasons={reasons} className="font-medium text-sm" />
              );
              return badge;
            })()}
          </>
        ),
      },
      {
        id: 'faulty_components',
        header: () => <div className="whitespace-nowrap">Impacted Components</div>,
        accessorKey: 'faulty_components',
        size: 30,
        enableSorting: false,
        cell: ({ row }) => (
          <>
            {(() => {
              const isRequireAttention = true; //TODO: need to filter rows that are isRequireAttention
              return (
                isRequireAttention && (
                  <FooterSection
                    isRequireAttention={isRequireAttention}
                    asset={row.original}
                    className="text-sm font-medium"
                  />
                )
              );
            })()}
          </>
        ),
      },
      {
        id: 'sensor_status',
        header: () => <div className="whitespace-nowrap">Sensor Status</div>,
        accessorKey: 'sensor_status',
        size: 30,
        maxSize: 105,
        minSize: 105,
        enableSorting: false,
        cell: ({ row }) => (
          <>
            {(() => {
              return (
                <div className="w-fit">
                  <SensorsNewIndicator
                    asset={row.original}
                    isNA={row.original.health_status === HEALTH_STATUS.NOT_AVAILABLE || row.original.not_monitored}
                  />
                </div>
              );
            })()}
          </>
        ),
      },
      {
        id: 'addons',
        header: () => <div className="whitespace-nowrap">Addons</div>,
        accessorKey: 'addons',
        size: 30,
        maxSize: 84,
        minSize: 84,
        enableSorting: false,
        cell: ({ row }) => (
          <>
            {(() => {
              return <AssetActionsBar asset={row.original} />;
            })()}
          </>
        ),
      },
    ];
  }, [assetsType]);

  return { tableStructure: columns };
};
